import { useEffect, useMemo, useState } from "react";
import { useAuth } from "hooks/useAuth";
import { listActiveFdeAssignmentsForUser } from "services/fde/fdeRegistryService";

/**
 * Resolves the caller's primary FDE role for dashboard landing routes.
 */
export function useFdeRole() {
  const { user, organizationId, userData } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;

  useEffect(() => {
    let cancelled = false;
    if (!user?.uid) {
      setAssignments([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    listActiveFdeAssignmentsForUser({
      uid: user.uid,
      institutionId,
    })
      .then((rows) => {
        if (!cancelled) setAssignments(rows);
      })
      .catch((err) => {
        console.error("[useFdeRole] load failed", err);
        if (!cancelled) setAssignments([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.uid, institutionId]);

  const primaryRole = useMemo(() => {
    const priority = ["fde-dc", "fde-t", "fde-c"];
    for (const role of priority) {
      if (assignments.some((row) => row.role === role && row.status === "active")) {
        return role;
      }
    }
    return null;
  }, [assignments]);

  const landingPath = useMemo(() => {
    if (primaryRole === "fde-c") return "/dashboard/sessions";
    if (primaryRole === "fde-dc") return "/dashboard/tci-manager";
    if (primaryRole === "fde-t") return "/dashboard/prompts";
    return null;
  }, [primaryRole]);

  const fdeCTciIds = useMemo(
    () =>
      assignments
        .filter((row) => row.role === "fde-c" && row.status === "active")
        .map((row) => row.tciId)
        .filter(Boolean),
    [assignments],
  );

  return {
    assignments,
    primaryRole,
    landingPath,
    loading,
    isFdeC: assignments.some((row) => row.role === "fde-c" && row.status === "active"),
    isFdeDc: assignments.some((row) => row.role === "fde-dc" && row.status === "active"),
    isFdeT: assignments.some((row) => row.role === "fde-t" && row.status === "active"),
    fdeCTciIds,
  };
}

export function resolveFdeLandingPath(assignments = []) {
  const active = assignments.filter((row) => row.status === "active");
  if (active.some((row) => row.role === "fde-dc")) return "/dashboard/tci-manager";
  if (active.some((row) => row.role === "fde-t")) return "/dashboard/prompts";
  if (active.some((row) => row.role === "fde-c")) return "/dashboard/sessions";
  return null;
}
